import { useMeasurementsTables } from "@api/generated/measurements/measurements";
import type { AuditRow } from "@api/generated/model";
import type { TFunction } from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cellText } from "./format";

/** One change-log row in words: inserted / deleted, or the first changed columns with their old → new values. */
export function changeText(row: AuditRow, t: TFunction): string {
  if (row.operation !== "UPDATE") return t(`journal.op.${row.operation}`);
  const cols = Object.keys(row.new ?? {});
  const shown = cols.slice(0, 3).map((c) => `${c}: ${cellText(row.old?.[c])} → ${cellText(row.new?.[c])}`);
  return shown.join(" · ") + (cols.length > 3 ? ` · +${cols.length - 3}` : "");
}

/** A database table in Hungarian: a measurement table's own label, else the catalogue's (``tablak``), else its name. */
export function useTableText(): (table: string) => string {
  const { t } = useTranslation();
  const tables = useMeasurementsTables({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  const labels = useMemo(() => new Map((tables ?? []).map((x) => [x.name, x.label])), [tables]);
  return useCallback((table: string) => labels.get(table) ?? t(`tables.${table}`, { defaultValue: table }), [labels, t]);
}

/** A record's key in words: "ev 2026 · kis A · parc 12"; a generated uuid only by its first characters. */
export function recordKeyText(pk: Record<string, unknown> | null | undefined): string {
  if (!pk) return "—";
  return Object.entries(pk)
    .map(([k, v]) => `${k} ${typeof v === "string" && /^[0-9a-f-]{32,36}$/.test(v) ? `${v.slice(0, 8)}…` : cellText(v)}`)
    .join(" · ");
}
